import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  Copy,
  Check,
  BarChart3,
  Layout,
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { profilePagesAPI } from '../services/api';
import { SHORT_LINK_BASE_URL } from '../config';
import { formatDate } from '../utils/helpers';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import Badge from './common/Badge';
import Table from './common/Table';
import EmptyState from './common/EmptyState';

const themeVariant = (theme) =>
  ({
    default: 'primary',
    dark: 'default',
    light: 'default',
    sunset: 'orange',
    ocean: 'info',
  }[theme] || 'default');

const ProfilePages = () => {
  const [profilePages, setProfilePages] = useState([]);
  const [pagination, setPagination] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { loading, error, executeApiCall } = useApi();
  const [data, setData] = useState(null);

  const fetchProfilePages = useCallback(async () => {
    const result = await executeApiCall(profilePagesAPI.getAll, {
      page: currentPage,
      limit: 10,
    });
    if (result.success) setData(result.data);
  }, [executeApiCall, currentPage]);

  useEffect(() => {
    fetchProfilePages();
  }, [fetchProfilePages]);

  useEffect(() => {
    if (data) {
      setProfilePages(data.pages || []);
      setPagination(data.pagination || {});
    }
  }, [data]);

  const handleCopyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {}
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Delete this profile page? This cannot be undone.')) return;
    try {
      const response = await profilePagesAPI.delete(pageId);
      if (response.success) fetchProfilePages();
      else alert('Error deleting profile page: ' + response.error);
    } catch {
      alert('Error deleting profile page');
    }
  };

  const getPageUrl = (username, pageName) =>
    `${SHORT_LINK_BASE_URL}/p/${username}/${pageName}`;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner text="Loading profile pages…" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        title="Couldn't load profile pages"
        message="Please try again in a moment."
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
            Profile pages
          </h1>
          <p className="mt-1.5 text-sm text-ink-500">
            Bundle multiple links into a single shareable page.
          </p>
        </div>
        <Link
          to="/profile-pages/create"
          className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-medium text-white shadow-soft hover:bg-ink-800 transition self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          New profile page
        </Link>
      </header>

      {profilePages.length === 0 ? (
        <EmptyState
          icon={Layout}
          title="No profile pages yet"
          description="Create a link-in-bio style page that bundles all your links in one place."
          action={
            <Link
              to="/profile-pages/create"
              className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-medium text-white shadow-soft hover:bg-ink-800 transition"
            >
              <Plus className="h-4 w-4" />
              Create your first page
            </Link>
          }
        />
      ) : (
        <>
          <Table>
            <Table.Head>
              <tr>
                <Table.Header>Page</Table.Header>
                <Table.Header>Title</Table.Header>
                <Table.Header>Theme</Table.Header>
                <Table.Header>Links</Table.Header>
                <Table.Header>Status</Table.Header>
                <Table.Header>Created</Table.Header>
                <Table.Header className="text-right pr-5">Actions</Table.Header>
              </tr>
            </Table.Head>
            <Table.Body>
              {profilePages.map((page) => {
                const pageUrl = getPageUrl(page.username, page.name);
                return (
                  <Table.Row key={page.id}>
                    <Table.Cell>
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-soft">
                          <Layout className="h-4 w-4" />
                        </span>
                        <div>
                          <p className="text-sm font-medium text-ink-900">
                            {page.name}
                          </p>
                          <p className="text-xs text-ink-500 font-mono">
                            /p/{page.username}/{page.name}
                          </p>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-ink-800 line-clamp-1 max-w-xs">
                        {page.title}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={themeVariant(page.theme)}>
                        {page.theme}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="font-semibold text-ink-900">
                        {page.link_count || 0}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        variant={page.is_active ? 'success' : 'danger'}
                        dot
                      >
                        {page.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="text-sm text-ink-500">
                      {formatDate(page.created_at)}
                    </Table.Cell>
                    <Table.Cell className="text-right pr-3">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          onClick={() =>
                            handleCopyToClipboard(pageUrl, `url-${page.id}`)
                          }
                          title="Copy page URL"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-brand-600 hover:bg-brand-50"
                        >
                          {copiedId === `url-${page.id}` ? (
                            <Check className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                        <a
                          href={pageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open page"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-emerald-600 hover:bg-emerald-50"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <Link
                          to={`/profile-pages/${page.id}/analytics`}
                          title="Analytics"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-violet-600 hover:bg-violet-50"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/profile-pages/${page.id}/edit`}
                          title="Edit"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDeletePage(page.id)}
                          title="Delete"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-rose-600 hover:bg-rose-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-ink-500">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage(
                      Math.min(pagination.totalPages, currentPage + 1)
                    )
                  }
                  disabled={currentPage === pagination.totalPages}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfilePages;
